import React, { FC, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';
import {
  DeviceCalibration,
  Pitch,
  perspectiveTransformProblem,
} from '@intelligent-play-v2/domain';

import { getDeviceCalibrations, patchDevice } from '~/api';
import { useAuthStore } from '~/stores';
import { isVivacityUser } from '~/utils';
import { Button, ButtonType } from '~/components/button';
import { Loading } from '~/components/loading';
import { PerspectiveTransformField } from '~/components/admin/onboarding';

interface AdminFacilitySensorsProps {
  facilityId: number;
  pitches: Pitch[];
}

const sameTransform = (a: number[] | null, b: number[] | null): boolean =>
  JSON.stringify(a) === JSON.stringify(b);

interface SensorCalibrationRowProps {
  device: DeviceCalibration;
  facilityId: number;
}

/**
 * One device: its transform, editable, with its own save.
 *
 * Each row saves independently because a transform is a per-sensor measurement — there
 * is no sense in which "save all" is safer, and a reviewer correcting one camera should
 * not have to re-confirm the others.
 */
const SensorCalibrationRow: FC<SensorCalibrationRowProps> = ({
  device,
  facilityId,
}) => {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<number[] | null>(
    device.perspectiveTransform
  );

  useEffect(() => {
    // a fresh read (after a save, or another admin's edit) replaces the draft
    setDraft(device.perspectiveTransform);
  }, [device.perspectiveTransform]);

  const { mutate: save, isLoading: isSaving } = useMutation(patchDevice, {
    onSuccess: () => {
      toast.success(`Sensor ${device.id} perspective transform saved`);
      queryClient.invalidateQueries(['deviceCalibrations', facilityId]);
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      toast.error(
        error.response?.data?.message ??
          `Could not save sensor ${device.id}. Please try again.`
      );
    },
  });

  const isValid = draft !== null && perspectiveTransformProblem(draft) === null;
  const isChanged = !sameTransform(draft, device.perspectiveTransform);

  return (
    <div className="p-4 border rounded border-primary-250">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-base font-semibold">
          Sensor {device.id}
          {!device.isActive && (
            <span className="ml-2 text-sm font-normal text-primary-500">
              (inactive)
            </span>
          )}
        </p>
        <p className="text-sm text-primary-500">
          {device.lensSpecMillimeters === null
            ? 'Lens spec not recorded'
            : `Lens ${device.lensSpecMillimeters}mm`}
          {' · last updated '}
          {new Date(device.updatedAt).toLocaleDateString()}
        </p>
      </div>
      <div className="mt-3">
        <PerspectiveTransformField
          label="Perspective transform"
          value={draft}
          onChange={setDraft}
        />
      </div>
      <div className="flex items-center justify-end mt-3 space-x-3">
        {isChanged && (
          <button
            type="button"
            className="text-sm font-semibold text-primary-500"
            onClick={() => setDraft(device.perspectiveTransform)}
            disabled={isSaving}
          >
            Discard changes
          </button>
        )}
        <div className="w-40">
          <Button
            text="Save transform"
            size="small"
            type={ButtonType.Secondary}
            disabled={!isValid || !isChanged}
            isLoading={isSaving}
            onClick={() => {
              if (draft) {
                save({ deviceId: device.id, perspectiveTransform: draft });
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};

/**
 * Sensor calibration for every device on the facility, grouped by pitch.
 *
 * Read from `GET /devices` rather than the facilities payload: that payload is cached for
 * 15 minutes and does not carry the transform, and an admin who has just saved a matrix
 * needs to see the saved matrix.
 */
export const AdminFacilitySensors: FC<AdminFacilitySensorsProps> = ({
  facilityId,
  pitches,
}) => {
  const { user } = useAuthStore();
  // calibration is ours to get right, not a customer admin's to change
  const isInternal = isVivacityUser(user);

  const { data: devices, isLoading, isError } = useQuery(
    ['deviceCalibrations', facilityId],
    async () => getDeviceCalibrations(facilityId),
    { enabled: isInternal }
  );

  if (!isInternal) {
    return null;
  }

  const orderedPitches = [...pitches].sort(
    (a, b) => (a.facilityPitchNumber ?? 0) - (b.facilityPitchNumber ?? 0)
  );
  const knownPitchIds = new Set(pitches.map(pitch => pitch.id));
  // a device whose pitch is not in the cached payload — stale cache, or a hidden pitch
  const orphans = (devices ?? []).filter(
    device => !knownPitchIds.has(device.pitchId)
  );

  return (
    <div className="mt-10 text-primary-900 bg-white rounded shadow-250">
      <div className="px-5 py-3 border-b border-primary-100">
        <div className="text-lg font-semibold">Sensors</div>
        <p className="mt-1 text-sm text-primary-500">
          The perspective transform maps each sensor&apos;s view onto the pitch.
          Detections use the saved matrix from the next pipeline refresh, so a
          wrong one shows up in heatmaps straight away — check it before saving.
        </p>
      </div>
      <div className="p-5 space-y-6">
        {isLoading && <Loading />}
        {isError && (
          <p className="text-sm text-red-dark">
            Could not load the sensors for this facility.
          </p>
        )}
        {devices && devices.length === 0 && (
          <p className="text-sm text-primary-500">
            No sensors are recorded for this facility.
          </p>
        )}
        {devices &&
          orderedPitches.map(pitch => {
            const pitchDevices = devices.filter(
              device => device.pitchId === pitch.id
            );
            if (pitchDevices.length === 0) {
              return null;
            }
            return (
              <div key={pitch.id}>
                <p className="text-base font-semibold">
                  {pitch.facilityPitchNumber !== null
                    ? `${pitch.facilityPitchNumber}. `
                    : ''}
                  {pitch.name}
                </p>
                <div className="mt-2 space-y-3">
                  {pitchDevices.map(device => (
                    <SensorCalibrationRow
                      key={device.id}
                      device={device}
                      facilityId={facilityId}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        {orphans.length > 0 && (
          <div>
            <p className="text-base font-semibold">Other sensors</p>
            <p className="text-sm text-primary-500">
              On pitches not in the current facility listing.
            </p>
            <div className="mt-2 space-y-3">
              {orphans.map(device => (
                <SensorCalibrationRow
                  key={device.id}
                  device={device}
                  facilityId={facilityId}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
